import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import * as Yup from 'yup';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { ErrorWord, } from 'views/templates/accountOpen/common';
import { SymbolPageStep } from '../../../hooks';
import { DomesticRealEstateSymbolDetailInfo } from 'views/utils/graphql/graphql';

export type InputMeetingDateTimeProps = {
  meetingDate1: Date | null;
  meetingTime1: string;
  meetingDate2: Date | null;
  meetingTime2: string;
  meetingDate3: Date | null;
  meetingTime3: string;
};

type InputITExplanationDateTimeProps = {
  onClosed : (nextSymbolPageStep : SymbolPageStep, meetingDateTime? : Partial<InputMeetingDateTimeProps>) => void;
  symbolDetail : DomesticRealEstateSymbolDetailInfo;
  today: Date;
};

export const useInputITExplanationDateTime = ({ onClosed, symbolDetail, today }: InputITExplanationDateTimeProps) => {
  const { showError, messageClose } = useGlobalToastHandler();

  const validation = Yup.object().shape({
      meetingDate1: Yup.date().required(ErrorWord.Date),
      meetingTime1: Yup.string().required(ErrorWord.SELECT),
      meetingDate2: Yup.date().required(ErrorWord.Date),
      meetingTime2: Yup.string().required(ErrorWord.SELECT),
      meetingDate3: Yup.date().required(ErrorWord.Date),
      meetingTime3: Yup.string().required(ErrorWord.SELECT),
    });

  const onSubmit = useOnceSubmit<InputMeetingDateTimeProps>(
    {
    //   onPreviousSubmit: async (input) => {
    //     return canApplyItExplanation(symbolDetailInfo.data?.GetSymbolInfo, input);
    //   },
      onSubmit: async (input) => {
        onClosed(SymbolPageStep.Confirm, input);
      },
    },
    [showError, messageClose],
  );

  const formik = useFormik<InputMeetingDateTimeProps>({
    initialValues: {
      meetingDate1: null,
      meetingTime1: '',
      meetingDate2: null,
      meetingTime2: '',
      meetingDate3: null,
      meetingTime3: '',
    },
    onSubmit,
    validationSchema: validation,
    validateOnBlur: true,
    validateOnChange: true,
  });

  const createDate = ({addDays, limitDate}:{addDays: number, limitDate?: Date}) => {
    const date = new Date(today);
    date.setDate(today.getDate() + addDays)
    return limitDate && limitDate < date ? limitDate : date;
  }
  const minDate = createDate({addDays: Number(symbolDetail.ITExplanationMinDate)})
  const maxDate = createDate({addDays: Number(symbolDetail.ITExplanationMaxDate), limitDate: symbolDetail.ITExplanationLimitDate ? new Date(symbolDetail.ITExplanationLimitDate!) : today})
  const timeOptions = [
    '10:00 ~ 11:00',
    '11:00 ~ 12:00',
    '12:00 ~ 13:00',
    '13:00 ~ 14:00',
    '14:00 ~ 15:00',
    '15:00 ~ 16:00',
    '16:00 ~ 17:00',
  ]

  const meetingDateTime1Props = {
    dateInputProps : {
      date: formik.values.meetingDate1,
      name: 'meetingDate1',
      minDate: minDate,
      maxDate: maxDate,
      onChange: (value: any) => {
        formik.setFieldValue('meetingDate1', value)
      },
      onBlur: formik.handleBlur,
      isWeekEndDisable: true,
      error: formik.touched.meetingDate1 ? formik.errors.meetingDate1 : '',
    },
    selectorProps : {      
      selectedOption: formik.values.meetingTime1,
      options: timeOptions,
      disabledOption : "時間を選択",
      setSelectedOption: (value: string) => {
        formik.setFieldValue('meetingTime1', value);
      },    
      error: formik.touched.meetingTime1 ? formik.errors.meetingTime1 : '',
    }
  }
  const meetingDateTime2Props = {
    dateInputProps : {
      date: formik.values.meetingDate2,
      name: 'meetingDate2',
      minDate: minDate,
      maxDate: maxDate,
      onChange: (value: any) => {
        formik.setFieldValue('meetingDate2', value)
      },
      onBlur: formik.handleBlur,
      isWeekEndDisable: true,
      error: formik.touched.meetingDate2 ? formik.errors.meetingDate2 : '',
    },
    selectorProps : {      
      selectedOption: formik.values.meetingTime2,
      options: timeOptions,
      disabledOption : "時間を選択",
      setSelectedOption: (value: string) => {
        formik.setFieldValue('meetingTime2', value);
      },
      error: formik.touched.meetingTime2 ? formik.errors.meetingTime2 : '',
    }
  }
  const meetingDateTime3Props = {
    dateInputProps : {
      date: formik.values.meetingDate3,
      name: 'meetingDate3',
      minDate: minDate,
      maxDate: maxDate,
      onChange: (value: any) => {
        formik.setFieldValue('meetingDate3', value)
      },
      onBlur: formik.handleBlur,
      isWeekEndDisable: true,
      error: formik.touched.meetingDate3 ? formik.errors.meetingDate3 : '',
    },
    selectorProps : {
      selectedOption: formik.values.meetingTime3,
      options: timeOptions,
      disabledOption : "時間を選択",
      setSelectedOption: (value: string) => {
        formik.setFieldValue('meetingTime3', value);
      },
      error: formik.touched.meetingTime3 ? formik.errors.meetingTime3 : '',
    }
  }

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    meetingDateTime1Props,
    meetingDateTime2Props,
    meetingDateTime3Props,
  };
};
